// React Query hooks for the time-slots settings page.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTimeSlot,
  deleteTimeSlot,
  listTimeSlots,
  updateTimeSlot,
} from '../../../../api/timeSlots';
import { queryKeys } from '../../../../api/queryKeys';
import { useToast } from '../../../../hooks/useToast';
import type {
  CreateTimeSlotInput,
  TimeSlotDto,
  UpdateTimeSlotInput,
} from '../../../../types/api';

export function useTimeSlotsByType(type: 'weekday' | 'weekend') {
  return useQuery<TimeSlotDto[]>({
    queryKey: queryKeys.timeSlots.list(type),
    queryFn: () => listTimeSlots(type),
    staleTime: 60_000,
  });
}

export function useCreateTimeSlot() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: CreateTimeSlotInput) => createTimeSlot(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.timeSlots.all });
      toast.success('Time slot added.');
    },
    onError: () => {
      toast.error('Failed to add time slot.');
    },
  });
}

export function useUpdateTimeSlot() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: number;
      input: UpdateTimeSlotInput;
    }) => updateTimeSlot(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.timeSlots.all });
      toast.success('Time slot updated.');
    },
    onError: () => {
      toast.error('Failed to update time slot.');
    },
  });
}

export function useDeleteTimeSlot() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (id: number) => deleteTimeSlot(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.timeSlots.all });
      toast.success('Time slot deleted.');
    },
    onError: () => {
      toast.error('Failed to delete time slot.');
    },
  });
}
